import { useEffect, useState } from 'react';

import moment from 'moment/moment';

import SearchAsset from '../SearchAsset';
import { DatePicker } from '../../form';
import { useUserStrategies } from '../../../context/UserStrategies';
import { useIntervals } from '../../../hooks/useIntervals';
import {
  computePercentage,
  getDataForResponsiveLine,
  getEndDate,
  getStartEndDates,
  getSymbol,
} from '../../../helpers/functions';
import { ChartLine } from '../../Strategies/ChartLine';
import { COLORS_HEX } from '../../../constants/colors';

import { IAsset, TTickerCurrency } from '../../../types/model';
import { AssetPerformanceWithStrategyModel } from '../types';
import { useLazyQuery } from '@apollo/client';
import { GET_ASSET } from '../../../http/apollo-queries';
import { Loading } from '../../UI';
import { LineChartData } from '../../Strategies/types';
import { CurrencyCodeEnum } from '../../../constants/enums';

export const CompareMyPortfolio = ({
  width,
  strategyId,
}: AssetPerformanceWithStrategyModel) => {
  const { strategies } = useUserStrategies();

  const {
    interval,
    IntervalSelector,
    handleInterval: resetInterval,
  } = useIntervals();

  const strategy = strategies.find(({ id }) => id === strategyId);
  const {
    color,
    name,
    currency: strategyCurrency,
    createdAt,
    strategyAssets: { items: assets },
  } = strategy!;

  const currency = strategyCurrency.toLocaleLowerCase() as TTickerCurrency;
  const initialAsset = strategy?.strategyAssets?.items?.[0]?.asset;

  const [[startDate, endDate], setDates] = useState([
    new Date(moment().subtract(1, 'd').format('YYYY/MM/DD')),
    getEndDate(),
  ]);
  const [selectedAssetId, setSelectedAssetId] = useState(initialAsset?.id);
  const [asset, setAsset] = useState(initialAsset);
  const [grData, setGrData]: any = useState(null);

  const [getAsset, { loading }] = useLazyQuery<{
    getAsset: IAsset;
  }>(GET_ASSET);

  const handleCalendar = (newDates: [Date, Date]) => {
    setDates(newDates);
    resetInterval();
  };

  useEffect(() => {
    if (interval !== 'VAR_24H') {
      const { endDate, startDate } = getStartEndDates(interval);

      setDates([startDate, endDate]);
      getAssetHistory();
    }
  }, [interval]);

  useEffect(() => {
    getAssetHistory();
  }, [selectedAssetId, startDate, endDate]);

  const strategyData = getDataForResponsiveLine({
    assets,
    color,
    currency,
    endDate: moment(endDate).format('YYYY-MM-DD'),
    name,
    startDate: moment(startDate).format('YYYY-MM-DD'),
    strategyCreatedAt: createdAt,
    usePercentage: true,
    useUnit: true,
  });

  const getAssetHistory = () => {
    const start = startDate.toISOString().split('T')[0];
    const end = endDate.toISOString().split('T')[0];

    if (strategyData.data.length === 0) {
      return;
    }
    const absoluteValues: number[] = [];
    const percentageValues: number[] = [];

    const data = (asset?.history ?? [])
      .filter(
        ({ date }) =>
          date &&
          dateCheck(start, end, new Date(date).toISOString().split('T')[0])
      )
      .map((item) => ({
        x: item.date,
        y: item[currency],
      }))
      .filter(
        ({ x }) =>
          moment(x).format('YYYY-MM-DD').localeCompare(strategyData.data[0].x) >
          -1
      )
      .sort((a, b) => a.x.localeCompare(b.x))
      .map(({ x, y }, _, arr) => {
        const percentage = computePercentage(arr[0].y, y, 100);
        absoluteValues.push(y);
        percentageValues.push(percentage);

        return {
          x: moment(x).format('YYYY-MM-DD'),
          y: percentage,
        };
      });

    const newGrData: LineChartData = {
      id: asset?.name ?? 'Asset',
      color: COLORS_HEX[strategy!.color === 'BLUE' ? 'TEAL' : 'BLUE'],
      data,
      absoluteValues,
      percentageValues,
    };

    setGrData(newGrData);
  };

  const dateCheck = (from, to, check) => {
    let fDate, lDate, cDate;
    fDate = Date.parse(from);
    lDate = Date.parse(to);
    cDate = Date.parse(check);
    return cDate <= lDate && cDate >= fDate;
  };

  const handleChoose = (asset) => {
    getAsset({ variables: { id: asset.id } }).then(
      ({ data: { getAsset: asset } = {} }) => {
        setAsset(asset);
        setSelectedAssetId(asset!.id);
      }
    );
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div
        className={`
          flex
          w-full
          items-center
          pl-5
                        h-16
          border-b
          border-grey-10
          dark:border-grey-80
        `}
      >
        <span className="flex items-center gap-2">
          <SearchAsset onChoose={handleChoose} defaultSelected={asset} />
        </span>

        {width !== 1 && (
          <div
            className={`
              flex
              items-center
              ml-auto
              border
              border-grey-10
              h-14
              dark:border-grey-80
              px-3
            `}
          >
            <DatePicker
              size={'medium'}
              id={'compareMyPortfolioDate'}
              isRangePicker={true}
              hasBorderBottom={false}
              startDate={startDate}
              endDate={endDate}
              onChange={(range) => handleCalendar(range as [Date, Date])}
              maxDate={new Date()}
            />
          </div>
        )}
        <IntervalSelector
          className={`
            ${
              width === 1 &&
              'ml-auto border-l border-grey-10 dark:border-grey-70'
            }
          `}
        />
      </div>

      {moment().format('YYYY-MM-DD') === createdAt ? (
        <div className="container mx-auto px-4 py-4 h-96 text-center">
          First data available after 24 hours from strategy creation!
        </div>
      ) : strategyData?.data && grData?.data ? (
        <div className={'w-full h-240 overflow-hidden'}>
          {/* CHART */}
          {loading ? (
            <div className={'flex pt-24 items-center justify-center'}>
              <Loading status={true} />
            </div>
          ) : (
            <ChartLine
              data={[strategyData, grData]}
              height="full"
              hideAxis={width === 1}
              suffix={getSymbol(strategy?.currency ?? CurrencyCodeEnum.USD)}
              usePercentage={true}
              useVariation={true}
            />
          )}
        </div>
      ) : (
        <p className="m-auto">No data available</p>
      )}
    </div>
  );
};
